import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { addParticipantBarrier } from '../../../store/participantService';
import { clearKeys } from '../../../utils/objectUtils';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import FormActions from '../../ui/formActions/FormActions';

import { validate } from './validateBarriers';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const CreateBarrier = ({ existingBarriers, closeEdit, cancelEdit, isDisabled }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        participantId: '',
        id: '',
        barrierId: null,
        startDate: String(new Date().toISOString().split('T')[0]),
        dueDate: String(new Date().toISOString().split('T')[0]),
        priority: 'Medium'
    };
    const message = 'Participant barrier added';
    const [newEntry, setNewEntry] = useState(initialState);
    const [arrayBarriers, setArrayBarriers] = useState([]);
    const [keys, setKeys] = useState({ barrierId: '0' });
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const barrierNameDetails = useSelector(
        (state) => state.entities.directusService.barrierNameDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USEEFFECTS
    useEffect(() => {
        if (currentParticipant?.id) {
            setNewEntry((prev) => ({ ...prev, participantId: currentParticipant.id }));
        }
    }, [currentParticipant]);

    useEffect(() => {
        if (barrierNameDetails?.length < 1) return;
        const updatedBarriers = existingBarriers.length
            ? barrierNameDetails.filter(
                  (el) =>
                      !existingBarriers.find(
                          (entry) => entry.barrierId === el.id && !entry.completeDate
                      )
              )
            : barrierNameDetails;
        setArrayBarriers(updatedBarriers);
    }, [barrierNameDetails]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearData = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setErrors({});
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onStartDateUpdate = (key, value) => {
        newEntry.dueDate && clearError('dueDate');
        onUpdate(key, value);
    };

    const onDueDateUpdate = (key, value) => {
        newEntry.startDate && clearError('startDate');
        onUpdate(key, value);
    };

    const onClose = () => {
        clearData();
        closeEdit();
    };

    const onCancel = () => {
        clearData();
        cancelEdit();
    };

    // FORM SUBMIT
    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, 'create', currentParticipant.referralDate);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(addParticipantBarrier(newEntry, message));
        onClose();
    };

    // RENDER
    return (
        <form onSubmit={onSubmit} data-testid="form_start_edit_barrier">
            <h2 className={app.sectionHeading}>Create a Barrier</h2>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <SingleSelect
                        id="barrierId"
                        key={keys.barrierId}
                        label="Barrier Name"
                        placeholder="Select a barrier name"
                        disabled={isDisabled}
                        mandatory={true}
                        menuItems={arrayBarriers || []}
                        selectedId={newEntry?.barrierId}
                        selected={arrayBarriers?.find((el) => el.id === newEntry.barrierId) || {}}
                        error={errors.barrierId}
                        onChange={(chosenId) => onUpdate('barrierId', chosenId)}
                    />
                </div>
                <div className={form.formColumn}>
                    <RadioButtons
                        id="priority"
                        label="What is the barrier priority?"
                        disabled={isDisabled}
                        radioButtons={[
                            { id: 'Low', label: 'Low', onChangeVal: 'Low' },
                            { id: 'Medium', label: 'Medium', onChangeVal: 'Medium' },
                            { id: 'High', label: 'High', onChangeVal: 'High' }
                        ]}
                        value={newEntry.priority || ''}
                        onChange={(value) => onUpdate('priority', value)}
                    />
                    <div className={form.formColumnSplit}>
                        <DateSelect
                            id="startDate"
                            label="Start Date"
                            disabled={isDisabled}
                            mandatory={true}
                            value={newEntry.startDate}
                            error={errors.startDate}
                            onDateChange={(date) => onStartDateUpdate('startDate', date)}
                        />
                        <DateSelect
                            id="dueDate"
                            label="Due Date"
                            disabled={isDisabled}
                            mandatory={true}
                            value={newEntry.dueDate}
                            error={errors.dueDate}
                            onDateChange={(date) => onDueDateUpdate('dueDate', date)}
                        />
                    </div>
                </div>
            </div>
            <FormActions
                onClose={onSubmit}
                onCancel={onCancel}
                disabled={isButtonDisabled || isDisabled}
                btnText={'Add Barrier'}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};
CreateBarrier.propTypes = {
    existingBarriers: PropTypes.arrayOf(PropTypes.object),
    closeEdit: PropTypes.func,
    cancelEdit: PropTypes.func,
    isDisabled: PropTypes.bool
};

export default CreateBarrier;
